import {
    Directive,
    ElementRef,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
} from '@angular/core';
import { AbstractControl, NgControl, ValidationErrors } from '@angular/forms';
import { DjangoService } from '@core/services/django.service';
import { EnvironmentService } from '@core/services/environment.service';
import intlTelInput, {
    type Iso2,
    type Iti,
    type SomeOptions,
} from 'intl-tel-input';
import { combineLatest, firstValueFrom, from } from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    startWith,
    take,
} from 'rxjs/operators';

@Directive({
    selector: '[ng2TelInput]',
    host: {
        '(blur)': 'onBlur()',
        '(input)': 'onInput()',
    },
})
export class Ng2TelInputDirective implements OnInit, OnDestroy {
    private elementRef = inject<ElementRef<HTMLInputElement>>(ElementRef);
    private environmentService = inject(EnvironmentService);
    private ngControl = inject(NgControl);
    private djangoService = inject(DjangoService);

    ng2TelInputOptions = input<SomeOptions>({});
    hasError = output<boolean>();

    private ngTelInput: Iti;
    private isPhoneInputReady = false;
    private readonly handleCountryChange = () => this.onCountryChange();

    private async lookupCountryCode(): Promise<Iso2> {
        try {
            const data = await firstValueFrom(this.djangoService.country());
            const countryCode = data.country?.toLowerCase();

            if (this.isSupportedCountryCode(countryCode)) {
                return countryCode;
            }

            return 'gb';
        } catch {
            return 'gb';
        }
    }

    private isSupportedCountryCode(
        countryCode: string | null | undefined
    ): countryCode is Iso2 {
        return intlTelInput
            .getAllCountries()
            .some(country => country.iso2 === countryCode);
    }

    ngOnInit() {
        if (!this.environmentService.isBrowser()) {
            return;
        }

        this.initializePhoneInput();
        this.addPhoneNumberValidator();
        this.syncInitialControlValue();
        this.elementRef.nativeElement.addEventListener(
            'countrychange',
            this.handleCountryChange
        );
    }

    ngOnDestroy() {
        if (!this.ngTelInput) {
            return;
        }

        this.elementRef.nativeElement.removeEventListener(
            'countrychange',
            this.handleCountryChange
        );
        this.ngTelInput.destroy();
    }

    private initializePhoneInput() {
        this.ngTelInput = intlTelInput(this.elementRef.nativeElement, {
            ...this.ng2TelInputOptions(),
            initialCountryLookup: () => this.lookupCountryCode(),
            numberDisplayFormat: 'INTERNATIONAL',
            separateDialCode: false,
            strictMode: false,
            loadUtils: () => import('intl-tel-input/utils'),
        });

        this.ngTelInput.promise.then(() => {
            this.isPhoneInputReady = true;

            if (!this.ngControl.control.value) {
                this.ngControl.control.updateValueAndValidity();
            }
        });
    }

    private addPhoneNumberValidator() {
        this.ngControl.control.addValidators(
            this.validatePhoneNumber.bind(this)
        );
    }

    private syncInitialControlValue() {
        combineLatest([
            this.ngControl.control.valueChanges.pipe(
                startWith(this.ngControl.control.value)
            ),
            from(this.ngTelInput.promise),
        ])
            .pipe(
                map(([value]) => value),
                distinctUntilChanged(),
                filter(value => !!value),
                take(1)
            )
            .subscribe(value => {
                this.ngTelInput.setNumber(value);
                this.syncPhoneControl();
            });
    }

    onCountryChange() {
        // intl-tel-input emits countrychange before its follow-up input event,
        // so wait until the selected dial code and visible value have settled.
        queueMicrotask(() => this.syncPhoneControl(false));
    }

    validatePhoneNumber({ value }: AbstractControl): ValidationErrors | null {
        if (!this.isPhoneInputReady || value === '') {
            return null;
        }

        return this.ngTelInput.isValidNumberPrecise()
            ? null
            : { phonenumberInvalid: true };
    }

    onInput() {
        queueMicrotask(() => this.syncPhoneControl(false));
    }

    // add country prefix to input on blur
    onBlur() {
        this.syncPhoneControl();
    }

    private syncPhoneControl(emitModelToViewChange = true) {
        const inputValue = this.elementRef.nativeElement.value;

        if (inputValue === '') {
            this.ngControl.control.setValue('', {
                emitModelToViewChange,
            });
            return;
        }

        if (!this.isPhoneInputReady) {
            this.ngControl.control.setValue(inputValue, {
                emitModelToViewChange,
            });
            return;
        }

        if (this.ngTelInput.isValidNumberPrecise()) {
            this.ngControl.control.setValue(this.ngTelInput.getNumber(), {
                emitModelToViewChange,
            });
            return;
        }

        this.ngControl.control.setValue(inputValue, {
            emitModelToViewChange,
        });
    }
}
